/**
 * DOM side-effect handlers (dom:*).
 *
 * These run inside the browser renderer. They are defensive no-ops in
 * non-browser environments (SSR/tests) so flows stay portable.
 */

import type { FlowStep } from "../types/flow";
import { resolveValue } from "../expressions/resolve";
import { registerStepHandler } from "../runtime/step-registry";

function asRecord(step: FlowStep): Record<string, unknown> {
 return step as unknown as Record<string, unknown>;
}

function asScrollBehavior(value: unknown): ScrollBehavior {
 return value === "smooth" || value === "auto" ? value : "auto";
}

function asScrollPosition(
 value: unknown,
 fallback: ScrollLogicalPosition,
): ScrollLogicalPosition {
 if (
 value === "start" ||
 value === "center" ||
 value === "end" ||
 value === "nearest"
 ) {
 return value;
 }
 return fallback;
}

function nextFrame(): Promise<void> {
 return new Promise((resolve) => {
 if (typeof requestAnimationFrame === "function") {
 requestAnimationFrame(() => resolve());
 } else {
 setTimeout(resolve,16);
 }
 });
}

registerStepHandler("dom:scrollIntoView", async (step, ctx) => {
 if (typeof document === "undefined") return;

 const r = asRecord(step);
 const selectorValue = resolveValue(r["selector"], ctx);
 const selector =
 typeof selectorValue === "string" ? selectorValue.trim() : "";
 if (!selector) return;

 const behavior = asScrollBehavior(resolveValue(r["behavior"], ctx));
 const block = asScrollPosition(resolveValue(r["block"], ctx), "end");
 const inline = asScrollPosition(resolveValue(r["inline"], ctx), "nearest");

 // Wait one frame so React has committed the state mutations that ran
 // before this step (e.g. the freshly-pushed user message). Without this
 // the scroll height is still the pre-message height and we land short.
 await nextFrame();

 const target = document.querySelector(selector);
 if (!target) {
 ctx.logger.warn(`dom:scrollIntoView: no element matched "${selector}"`);
 return;
 }

 target.scrollIntoView({ behavior, block, inline });
});